import { Inject, Injectable } from '@angular/core';
import { Blame, BlameSource, OrderFulfillmentService, OrderItemParameters } from '@vendasta/order-fulfillment';
import { combineLatest, from, Observable, of, Subject } from 'rxjs';
import { catchError, last, map, mergeMap, scan, switchMap, take } from 'rxjs/operators';
import { AuthService, JWT } from '../../../core/auth/auth.service';
import { ActivateAppApiService } from './activate-app-api.service';
import { ActivationResult, Order } from './interface';

export interface OrderWithOrderFormSubmissionId {
  order: Order;
  submissionId: string;
}

interface OrderWithActivationAndSubmissionIds {
  order: Order;
  submissionId: string;
  activationId: string;
}

@Injectable()
export class ActivateAppService {
  readonly errors$: Observable<ActivationResult>;
  readonly successes$: Observable<ActivationResult>;

  private errors$$: Subject<ActivationResult> = new Subject<ActivationResult>();
  private successes$$: Subject<ActivationResult> = new Subject<ActivationResult>();
  private blameSource$: Observable<Blame>;
  constructor(
    private activateAppApiService: ActivateAppApiService,
    private orderFulfillmentService: OrderFulfillmentService,
    private authService: AuthService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
  ) {
    this.errors$ = this.errors$$.asObservable();
    this.successes$ = this.successes$$.asObservable();
    this.blameSource$ = this.authService.decodedAuthToken$.pipe(
      take(1),
      map((token: JWT) => {
        return token
          ? new Blame({
              source: BlameSource.BLAME_SOURCE_USER,
              sourceIdentifier: token ? token.sub : '',
            })
          : null;
      }),
    );
  }

  /**
   * Filters through a list of orders and transforms them into a list of orders with order form submission ids
   *
   * @param orders {Order[]} A list of orders
   * @return {Observable<OrderWithOrderFormSubmissionId[]>} An observable for a list of OrderWithOrderFormSubmissionId objects
   *
   */
  createArrayOfOrdersWithSubmissionId$(orders: Order[]): Observable<OrderWithOrderFormSubmissionId[]> {
    if (!orders || orders.length === 0) {
      throw Error('activateOrders calls with no orders');
    }

    const flatOrdersWithSubmissionId$ = from(orders).pipe(
      mergeMap((order) => {
        return this.submitOrderForm$(order).pipe(
          catchError((err) => {
            return of(
              this.convertErrorIntoActivationError(order.appId, order.businessId, order.name, err.message, err),
            );
          }),
        );
      }),
    );

    const arrayOfOrdersWithSubmissionIds$ = flatOrdersWithSubmissionId$.pipe(
      take(orders.length),
      scan((acc: OrderWithOrderFormSubmissionId[], curr: OrderWithOrderFormSubmissionId) => {
        acc.push(curr);
        return acc;
      }, []),
      last(),
    );

    return arrayOfOrdersWithSubmissionIds$;
  }

  /**
   * Activates apps using order fulfillment; only called when feature flag is set.
   *
   * @param orders {Order[]} A list of orders.
   *
   * @return {Observable<string>} An observable of a string which contains the order id.
   */
  public activateOrderWithOrderFulfillment(orders: Order[]): Observable<string> {
    const arrayOfOrdersWithSubmissionIds$ = this.createArrayOfOrdersWithSubmissionId$(orders);
    const orderItemParams$ = arrayOfOrdersWithSubmissionIds$.pipe(
      map((orderItems: OrderWithOrderFormSubmissionId[]) => {
        return orderItems
          .map((o) => {
            if (!o || !o.order) {
              return;
            }

            const { appId, businessId, editionId } = o.order;

            return {
              itemId: appId,
              orderFormSubmissionId: o.submissionId,
              businessId,
              editionId,
            } as OrderItemParameters;
          })
          .filter(Boolean);
      }),
    );

    return combineLatest([this.partnerId$, orderItemParams$]).pipe(
      take(1),
      switchMap(([pid, oip]) => this.createOrder(pid, oip)),
    );
  }

  /**
   * Activates apps using order fulfillment.
   *
   * @param partnerId - ID of the partner to create an order for.
   * @param orderItemParams - A list of order item parameters.
   *
   * @return {Observable<string>} An observable of a string which contains the order id.
   */
  public createOrder(partnerId: string, orderItemParams: OrderItemParameters[]): Observable<string> {
    return this.blameSource$.pipe(
      take(1),
      switchMap((blameSource) => {
        return this.orderFulfillmentService.createOrder(partnerId, orderItemParams, blameSource);
      }),
    );
  }

  /**
   * Submit the order form if the Order requires it.
   * If usesOrderForm is false, simply returns a result with no submission id
   * @param {Order} order
   * @returns {Observable<OrderWithOrderFormSubmissionId | ActivationResult>}
   */
  private submitOrderForm$(order: Order): Observable<OrderWithOrderFormSubmissionId | ActivationResult> {
    // Don't submit if the app doesn't use an order form
    if (!order.usesOrderForm) {
      return of({
        order: order,
        submissionId: '',
      });
    }

    return this.activateAppApiService.createOrderForm(order).pipe(
      map((submissionId) => {
        return {
          order: order,
          submissionId: submissionId,
        };
      }),
    );
  }

  private isActivationResult(result: ActivationResult): boolean {
    return (
      typeof result.isSuccess !== 'undefined' ||
      typeof result.error !== 'undefined' ||
      typeof result.errorMessage !== 'undefined'
    );
  }

  private convertOrderIntoActivationResult(
    order: OrderWithActivationAndSubmissionIds | ActivationResult,
  ): ActivationResult {
    if (this.isActivationResult(order as ActivationResult)) {
      return order as ActivationResult;
    }
    const o = (order as OrderWithActivationAndSubmissionIds).order;
    const result = {
      appId: o.appId,
      parentAppId: o.parentAppId,
      name: o.name,
      businessId: o.businessId,
      isSuccess: true,
    } as ActivationResult;
    if (o.editionId) {
      result.editionId = o.editionId;
    }
    return result;
  }

  private convertErrorIntoActivationError(
    appId: string,
    businessId: string,
    name: string,
    message: string,
    err: Error,
  ): ActivationResult {
    return {
      appId: appId,
      name: name,
      businessId: businessId,
      errorMessage: message,
      error: err,
      isSuccess: false,
    };
  }
}
